import React, { useRef, useEffect, useContext, useState, createRef } from 'react';
import './antares-horizontal-list.css';
import { withFocusable } from '../../spatial';
import { ParentNodeContextProvider } from '../antares-focusable/parent-node-context.jsx';
import spatialNavigation from '../../spatial/spatialNavigation';
import { ParentNodeContextConsumer } from '../antares-focusable/parent-node-context.jsx';
import { createUUID } from '../../utils/createUUID';
import PropTypes from 'prop-types';
import gsap from 'gsap/all';
import { PerformanceEnhancerContext } from '../../performance-enabler/performance-enabler.jsx';
import debounce from 'lodash/debounce';

/**
 * Antares Horizontal List component gives you the ability to create an horizontal focusable list.
 *
 */
const AntaresHorizontalList = (props) => {

    const [focused, setFocused] = useState(false);

    // const onBecameFocused = ({ width, height, x, y, top, left, node }, { prop1, prop2 }, { event, other }) => {
    //     setFocused(true);
    //     props.onFocus(node, spatialNavigation.getLastDirection());
    // }

    // const onBecameBlurred = ({ width, height, x, y, top, left, node }, { prop1, prop2 }, { event, other }) => {
    //     setFocused(false);
    //     props.onBlur(node, spatialNavigation.getLastDirection());
    // }

    // const onEnterPress = ({ width, height, x, y, top, left, node }, { prop1, prop2 }, { event, other }) => {
    //     props.onEnterDown(node);
    // }

    return (
        <ParentNodeContextConsumer>
            {
                params => {
                    if (params && params.type && params.type === 'grid')
                        return (
                            <GridFocusable
                                containerClassname={props.containerClassname}
                                innerClassname={props.innerClassname}
                                // onBecameFocused={onBecameFocused}
                                // onBecameBlurred={onBecameBlurred}
                                // onEnterPress={onEnterPress}
                                remainInFocus={params.remainInFocus}
                                animationDuration={props.animationDuration}
                                forgetLastFocusedChild={false}
                                focusKey={props.focusableId ? props.focusableId : `antares-grid-list-${createUUID()}`}
                                forceFocus={props.forceFocus}
                                scrollOffset={props.scrollOffset}
                                innerWidth={props.innerWidth}
                                fixed={props.overrideFixed !== null ? props.overrideFixed : params.fixed}
                                onViewportEnter={props.onViewportEnter}
                                onViewportExit={props.onViewportExit}
                                parentNodeId={params.parentNodeId}
                                preferredChildFocusKey={props.preferredChildFocusKey}
                                titleComponent={props.titleComponent}
                                focusable={params.isFocusable}
                                isParentFocused={focused}
                                isSquareGrid={props.isSquareGrid}
                                gridAnimationDelay={props.gridAnimationDelay}
                                onFocus={props.onFocus}
                                onBlur={props.onBlur}
                            >
                                {props.children}
                            </GridFocusable>
                        )

                    return (
                        <Focusable
                            containerClassname={props.containerClassname}
                            innerClassname={props.innerClassname}
                            remainInFocus={props.remainInFocus}
                            forgetLastFocusedChild={!props.retainLastFocus}
                            focusKey={props.focusableId ? props.focusableId : `antares-horizontal-list-${createUUID()}`}
                            forceFocus={props.forceFocus}
                            scrollOffset={props.scrollOffset}
                            animationDuration={props.animationDuration}
                            onViewportEnter={props.onViewportEnter}
                            onViewportExit={props.onViewportExit}
                            innerWidth={props.innerWidth}
                            preferredChildFocusKey={props.preferredChildFocusKey}
                            fixed={props.fixed}
                            titleComponent={props.titleComponent}
                            focusable={props.isFocusable}
                            isParentFocused={focused}
                            onFocus={props.onFocus}
                            onBlur={props.onBlur}
                            gridAnimationDelay={props.gridAnimationDelay}
                        >
                            {props.children}
                        </Focusable>
                    )
                }
            }
        </ParentNodeContextConsumer>
    );
}

const HorizontalListImplementation = (props) => {
    const nodeId = useRef(null);
    if (nodeId.current === null)
        nodeId.current = "focusable-" + createUUID();

    const performanceEnhancer = useContext(PerformanceEnhancerContext);

    useEffect(() => {
        if (props.forceFocus) {
            props.stealFocus();
        }
        return () => {
            nodeId.current = null;
        }
    }, []);


    useEffect(() => {
        if (props.hasFocusedChild) {
            if (props.onFocus){
                props.onFocus();
            }
        } else {
            if (props.onBlur)
                props.onBlur();
        }
    }, [props.hasFocusedChild])

    return (
        <ParentNodeContextProvider value={{ parentNodeId: `horizontal-list-container-${nodeId.current}`, parentInternalNodeId: `horizontal-inner-list-${nodeId.current}`, type: props.isSquareGrid ? 'vertical' : 'horizontal', fixed: props.fixed, scrollStep: props.scrollStep, direction: props.direction, animationDuration: props.animationDuration, remainInFocus: props.remainInFocus, isFocusable: props.isFocusable, gridAnimationDelay: props.gridAnimationDelay }}>
            {props.titleComponent ? props.titleComponent : null}
            <div id={`horizontal-list-container-${nodeId.current}`} className={`${props.containerClassname} horizontal-list-root-container ${performanceEnhancer.enhancePerformance ? `enhance-performace` : ``}`}>
                <div id={`horizontal-inner-list-${nodeId.current}`} className={`${props.innerClassname} horizontal-list-root `} style={{ minWidth: `${props.innerWidth}px` }}>
                    {props.children}
                </div>
            </div>
        </ParentNodeContextProvider>
    )
}

const GridListImplementation = (props) => {

    const nodeId = useRef(null);
    const parentNodeIdRef = useRef(null);

    if (nodeId.current === null)
        nodeId.current = "focusable-" + createUUID();

    const ref = useRef(null);

    const performanceEnhancer = useContext(PerformanceEnhancerContext);

    useEffect(() => {
        parentNodeIdRef.current = document.getElementById(props.parentNodeId);

        if (props.forceFocus) {
            props.stealFocus();
        }
        return () => {
            nodeId.current = null;
        }
    }, []);

    useEffect(() => {
        if (props.hasFocusedChild) {
            if (props.onFocus)
                props.onFocus();
            const debouncedAnimation = debounce(performAnimation, props.gridAnimationDelay);
            debouncedAnimation();
        } else {
            if (props.onBlur)
                props.onBlur();
        }
    }, [props.hasFocusedChild]);

    const performAnimation = () => {
        const boundingElement = ref.current.getBoundingClientRect();
        const boundingContainer = parentNodeIdRef.current.getBoundingClientRect();
        let isInView = !((boundingContainer.bottom < boundingElement.bottom) || (boundingContainer.top > boundingElement.top))
        if (props.fixed) {
            gsap.to(parentNodeIdRef.current, {
                duration: props.animationDuration,
                scrollTo: {
                    y: '#' + `horizontal-list-container-${nodeId.current}`,
                    offsetY: props.scrollOffset,
                    autoKill: false,
                    force3D: true
                }
            });
        }
        else {
            if (!isInView) {
                gsap.to(parentNodeIdRef.current, {
                    duration: props.animationDuration,
                    scrollTo: {
                        y: '#' + `horizontal-list-container-${nodeId.current}`,
                        offsetY: props.scrollOffset,
                        autoKill: false,
                        force3D: true,
                    }
                });
            }
        }
    }

    return (
        <ParentNodeContextProvider value={{ parentNodeId: `horizontal-list-container-${nodeId.current}`, type: 'horizontal', fixed: props.fixed, scrollStep: props.scrollStep, direction: props.direction, animationDuration: props.animationDuration, isFocusable: props.isFocusable }}>
            {props.titleComponent ? props.titleComponent : null}
            <div id={`horizontal-list-container-${nodeId.current}`} className={`horizontal-list-root-container ${props.containerClassname} ${performanceEnhancer.enhancePerformance ? `enhance-performace` : ``}`} ref={ref}>
                <div id={`horizontal-inner-list-${nodeId.current}`} className={`horizontal-list-root ${props.innerClassname}`} style={{ minWidth: `${props.innerWidth}px` }}>
                    {props.children}
                </div>
            </div>
        </ParentNodeContextProvider>
    )
}

AntaresHorizontalList.propTypes = {
    /** function executed when the item is focused. It takes index of the focused element, the node element and the last direction.*/
    onFocus: PropTypes.func,
    /** function executed when the item is blurred. It takes index of the blurred element, the node element and the last direction.*/
    onBlur: PropTypes.func,
    /** function executed when an enter keydown is performed and the item is focused. */
    onEnterDown: PropTypes.func,
    /** the classname you want to associate to the inner list component. */
    innerClassname: PropTypes.string,
    /** the classname you want to associate to the list container component. */
    containerClassname: PropTypes.string,
    /** focusable id. It's useful when imperative focus needs to be used. */
    focusableId: PropTypes.string,
    /** callback that is called when element is in viewport. */
    onViewportEnter: PropTypes.func,
    /** callback that is called when element exits the viewport. */
    onViewportExit: PropTypes.func,
    /** when the animation is not fixed, you can add a scrollOffset to place correctly the item into the screen. */
    scrollOffset: PropTypes.number,
    /** animation duration in seconds. */
    animationDuration: PropTypes.number,
    /** if the scroll animation is fixed or not.*/
    fixed: PropTypes.bool,
    /** if this property is true, then the spatial navigation will be suspended when the component is focused. The spatial navigation will be resumed when the component will be destroyed or when the funcion resumeSpatialNavigation will be called (see Others for more details). */
    remainInFocus: PropTypes.bool,
    /** if this property is a valid react component, it will a title above the list*/
    titleComponent: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.elementType
    ]),
    /** this prop defines the inner width dimension for the inner div container of the list.*/
    innerWidth: PropTypes.number
}

AntaresHorizontalList.defaultProps = {
    innerClassname: '',
    containerClassname: '',
    forceFocus: false,
    fixed: false,
    animationDuration: 0.5,
    retainLastFocus: false,
    onViewportEnter: () => { },
    scrollOffset: 0,
    onViewportExit: () => { },
    remainInFocus: false,
    innerWidth: 5000,
    overrideFixed: null,
    gridAnimationDelay: 100
};

const Focusable = withFocusable({
    trackChildren: true
})(HorizontalListImplementation);

const GridFocusable = withFocusable({
    trackChildren: true
})(GridListImplementation);

export default AntaresHorizontalList;
